import React from 'react';
import PropTypes from 'prop-types';
import _uniq from 'lodash/uniq';
import { CarrierCard, ZeroState } from '@itds/components';
import {
  Button,
  Checkbox,
  Input,
  FieldGroup,
  Field,
  Loader,
  Modal,
  Select
} from '@itds/elements/index.js';
import { FontSizes } from '@itds/Theme';
import { QUOTE_ACTIONS, FIRELIGHT_ACTIVITIES } from '@itds/Constants';

import Config from '@/config';

import { Api } from '@brokerage/utils/Api';

const anico_carrier_id = parseInt(Config.ANICO_CARRIER_ID, 10);
const centrian_carrier_id = parseInt(Config.CENTRIAN_CARRIER_ID, 10);
const liberty_mutual_carrier_id = parseInt(Config.LIBERTY_MUTUAL_CARRIER_ID, 10);
const nmb_carrier_id = parseInt(Config.NMB_CARRIER_ID, 10);
const paclife_carrier_id = parseInt(Config.PACLIFE_CARRIER_ID, 10);
const protective_carrier_id = parseInt(Config.PROTECTIVE_CARRIER_ID, 10);
const sbli_carrier_id = parseInt(Config.SBLI_CARRIER_ID, 10);

class EditQuoteActionModal extends React.Component {
  static propTypes = {
    actionTypes: PropTypes.array.isRequired,
    agency: PropTypes.object,
    availableCarrierIds: PropTypes.array,
    availableProductIds: PropTypes.array,
    beta: PropTypes.bool,
    hideIcon: PropTypes.bool,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    product_type: PropTypes.string,
    quoteAction: PropTypes.object,
    showFaceAmounts: PropTypes.bool,
    showOpenInModal: PropTypes.bool
  };

  constructor(props) {
    super(props);

    const quote_action = props.quoteAction || {
      label: '',
      action_type: 'firelight',
      carrier_ids: [],
      product_ids: [],
      action_data: {
        activity: 'e_app'
      }
    };

    this.state = {
      // action fields
      action_type: quote_action.action_type,
      carrier_ids: quote_action.carrier_ids,
      error_msg: quote_action.error_msg,
      label: quote_action.label,
      max_face_amount: quote_action.max_face_amount,
      min_face_amount: quote_action.min_face_amount,
      open_in_modal: quote_action.open_in_modal,
      order: quote_action.order,
      product_ids: quote_action.product_ids,
      success_msg: quote_action.success_msg,
      url: quote_action.url,

      // data
      carriers: [],
      filtered_carriers: [],
      viveCarriers: [],
      vive_filtered_carriers: [],
      firelight_carrier_ids: [],
      firelight_product_ids: [],
      firelight_products: [],

      // ui related
      errors: [],
      firelight_activity: quote_action.action_data ? quote_action.action_data.activity : 'e_app',
      firelight_loaded: false,
      firelight_error: false,
      icon: null,
      loading_carriers: true
    };
  }

  componentDidMount() {
    this.loadCarriersData();
  }

  componentDidUpdate(prevProps, prevState) {
    // Handle action type changes
    if (prevState.action_type !== this.state.action_type) {
      this.handleActionTypeChange();
    }

    // Handle quote action prop changes
    if (prevProps.quoteAction !== this.props.quoteAction) {
      this.handleQuoteActionChange();
    }
  }

  loadCarriersData = async () => {
    const { availableCarrierIds, availableProductIds, beta, quoteAction } = this.props;

    try {
      const [carriersResponse, agencyCarriersResponse] = await Promise.all([
        Api.loadLifeCarriers(beta),
        Api.loadAgencySpecificCarriers()
      ]);

      const allCarriers = carriersResponse.data || [];
      const agencyCarriers = agencyCarriersResponse.data || [];

      const filteredCarriers = allCarriers.filter(c => availableCarrierIds?.includes(c.id));
      const filteredAgencyCarriers = agencyCarriers.filter(c =>
        availableCarrierIds?.includes(c.id)
      );

      // Calculate initial IDs based on quote action or default to all
      const initialCarrierIds = quoteAction
        ? (quoteAction.carrier_ids || []).filter(id => availableCarrierIds?.includes(id))
        : filteredCarriers.map(c => c.id);

      const initialProductIds = quoteAction
        ? (quoteAction.product_ids || []).filter(id => availableProductIds?.includes(id))
        : filteredCarriers
            .flatMap(c => c.products.map(p => p.id))
            .filter(id => availableProductIds?.includes(id));

      this.setState(
        {
          loading_carriers: false,
          carriers: filteredCarriers,
          agency_specific_carriers: filteredAgencyCarriers,
          filtered_carriers: filteredCarriers,
          carrier_ids: initialCarrierIds,
          product_ids: initialProductIds
        },
        () => {
          // Load firelight products after state is set
          if (this.state.action_type === 'firelight') {
            this._getFirelightProducts();
          } else {
            this._handleFilterCarriers();
          }
        }
      );
    } catch (error) {
      this.setState({ loading_carriers: false });
    }
  };

  handleActionTypeChange = () => {
    // Use requestAnimationFrame to ensure state has been updated
    requestAnimationFrame(() => {
      if (this.state.action_type === 'firelight') {
        this._getFirelightProducts();
      } else {
        this._handleFilterCarriers();
      }
    });
  };

  handleQuoteActionChange = () => {
    const { quoteAction, availableCarrierIds, availableProductIds } = this.props;

    if (quoteAction) {
      this.setState(
        prevState => ({
          carrier_ids: (quoteAction.carrier_ids || []).filter(id =>
            availableCarrierIds?.includes(id)
          ),
          product_ids: (quoteAction.product_ids || []).filter(id =>
            availableProductIds?.includes(id)
          )
        }),
        () => {
          this._handleFilterCarriers();
        }
      );
    }
  };

  _getViveProducts = async () => {
    const { availableCarrierIds, availableProductIds } = this.props;

    // Prevent multiple concurrent requests
    if (this.state.loading_carriers) {
      return;
    }

    this.setState({
      loading_carriers: true,
      firelight_error: false,
      firelight_loaded: false
    });

    try {
      // Load Vive carriers
      const carriersResponse = await Api.loadViveCarriers();
      const allCarriers = carriersResponse.data || [];
      // Filter carriers based on available carrier IDs
      const filteredCarriers = allCarriers.filter(c => availableCarrierIds?.includes(c.id));
      // Extract all products from the carriers
      let allProducts = [];

      filteredCarriers.forEach(carrier => {
        if (carrier.products && carrier.products.length > 0) {
          // Add carrier_id to each product for consistency
          const carrierProducts = carrier.products.map(product => ({
            ...product,
            carrier_id: carrier.id
          }));

          allProducts = [...allProducts, ...carrierProducts];
        }
      });

      // Filter products by available product IDs if provided
      let firelight_product_ids = [];

      if (availableProductIds && allProducts.length > 0) {
        firelight_product_ids = allProducts
          .map(p => p.id)
          .filter(id => availableProductIds.includes(id));
      } else {
        // If no availableProductIds filter, use all products
        firelight_product_ids = allProducts.map(p => p.id);
      }

      const firelight_carrier_ids = filteredCarriers.map(c => c.id);

      this.setState(
        prevState => {
          // Only keep previously selected products that are valid Vive (term) products.
          // This prevents non-term products (carried over from other activities like e_app)
          // from being saved and incorrectly enabling simplified e-ticket for them.
          const validSelectedProductIds = prevState.product_ids.filter(id =>
            firelight_product_ids.includes(id)
          );

          return {
            firelight_product_ids,
            firelight_carrier_ids,
            firelight_products: allProducts,
            viveCarriers: filteredCarriers,
            vive_filtered_carriers: filteredCarriers,
            firelight_error: false,
            firelight_loaded: true,
            product_ids: validSelectedProductIds.length
              ? validSelectedProductIds
              : firelight_product_ids,
            loading_carriers: false
          };
        },
        () => {
          this._handleFilterCarriers();
        }
      );
    } catch (error) {
      console.error('Error in _getViveProducts (carriers API call failed):', error);
      console.error('Error details:', error.response?.data || error.message);

      this.setState({
        firelight_product_ids: [],
        firelight_carrier_ids: [],
        firelight_products: [],
        viveCarriers: [],
        vive_filtered_carriers: [],
        firelight_error: true,
        firelight_loaded: true,
        loading_carriers: false
      });
    }
  };

  _getFirelightProducts = async () => {
    const { agency, availableCarrierIds, availableProductIds } = this.props;

    if (!agency?.id) {
      return;
    }

    // Check if this is simplified_eticket activity
    const currentActivity = this.state.firelight_activity || 'e_app';

    if (currentActivity === 'simplified_eticket') {
      // Simplified e-ticket uses the Vive API exclusively.
      this._getViveProducts();
      return;
    }

    // Prevent multiple concurrent requests
    if (this.state.loading_carriers) {
      return;
    }

    this.setState({
      loading_carriers: true,
      firelight_error: false,
      firelight_loaded: false
    });

    try {
      // Use the original FireLight API for other activities
      const { data } = await Api.loadFirelightProducts(agency.id, currentActivity);

      let firelight_product_ids = [];

      if (availableProductIds && data) {
        firelight_product_ids = data.map(p => p.id).filter(id => availableProductIds.includes(id));
      }

      const firelight_carrier_ids = data
        ? _uniq(data.map(p => p.carrier_id).filter(id => availableCarrierIds?.includes(id)))
        : [];

      this.setState(
        prevState => {
          return {
            firelight_product_ids,
            firelight_carrier_ids,
            firelight_products: data || [],
            firelight_error: false,
            firelight_loaded: true,
            product_ids: Array.from(new Set([...prevState.product_ids, ...firelight_product_ids])),
            loading_carriers: false
          };
        },
        () => {
          this._handleFilterCarriers();
        }
      );
    } catch (error) {
      console.error('Error in _getFirelightProducts:', error);
      console.error('Error details:', error.response?.data || error.message);
      console.error('Current activity was:', currentActivity);

      this.setState({
        firelight_product_ids: [],
        firelight_carrier_ids: [],
        firelight_products: [],
        firelight_error: true,
        firelight_loaded: true,
        loading_carriers: false
      });
    }
  };

  _handleSaveClick = () => {
    const { quoteAction } = this.props;
    const {
      carrier_ids,
      action_type,
      error_msg,
      firelight_activity,
      label,
      max_face_amount,
      min_face_amount,
      open_in_modal,
      order,
      product_ids,
      success_msg,
      url
    } = this.state;
    const errors = [];

    if (!label) {
      errors.push('label');
    }

    if (!carrier_ids.length) {
      errors.push('carrier_ids');
    }

    this.setState({ errors });

    if (!errors.length) {
      // Final guard: only persist products that belong to the currently relevant
      // (selected) carriers. For simplified e-ticket this restricts the saved
      // products to the Vive (term) products, so non-term products can never be
      // enabled for simplified e-ticket.
      const isSimplifiedEticket =
        action_type === 'firelight' && firelight_activity === 'simplified_eticket';
      const relevantCarriers = isSimplifiedEticket
        ? this.state.vive_filtered_carriers
        : this.state.filtered_carriers;
      const selectedCarrierProductIds = relevantCarriers
        .filter(c => carrier_ids.includes(c.id))
        .flatMap(c => (c.products ? c.products.map(p => p.id) : []));
      const cleaned_product_ids = product_ids.filter(id => selectedCarrierProductIds.includes(id));

      const params = {
        id: quoteAction ? quoteAction.id : null,
        action_type,
        carrier_ids,
        error_msg,
        label,
        max_face_amount,
        min_face_amount,
        open_in_modal,
        order,
        product_ids: cleaned_product_ids,
        success_msg,
        url
      };

      if (action_type === 'firelight') {
        params.action_data = { activity: firelight_activity };
      }
      this.props.onSave(params);
    }
  };

  _handleSaveProducts = product_ids => {
    this.setState({
      product_ids
    });
  };

  _handleIconAdd = icon => {
    this.setState({
      icon
    });
  };

  _handleIconRemove = () => {
    this.setState({
      icon: null
    });
  };

  _handleFieldChange = (name, e) => {
    this.setState({
      [name]: e.target.value
    });
  };

  _handleCheckboxChange = (name, e) => {
    this.setState({
      [name]: e.target.checked
    });
  };

  _handleActionTypeChange = e => {
    this.setState({
      action_type: e.target.value
    });
  };

  _handleCarrierToggle = (carrier, checked) => {
    const is_selected = !checked;
    const carrier_product_ids = carrier.products ? carrier.products.map(p => p.id) : [];

    this.setState(prevState => {
      let newCarrierIds = [...prevState.carrier_ids];
      let newProductIds = [...prevState.product_ids];

      if (is_selected) {
        // Add carrier and its products
        if (!newCarrierIds.includes(carrier.id)) {
          newCarrierIds.push(carrier.id);
        }
        carrier_product_ids.forEach(productId => {
          if (!newProductIds.includes(productId)) {
            newProductIds.push(productId);
          }
        });
      } else {
        // Remove carrier and its products
        newCarrierIds = newCarrierIds.filter(id => id !== carrier.id);
        newProductIds = newProductIds.filter(id => !carrier_product_ids.includes(id));
      }

      return {
        carrier_ids: newCarrierIds,
        product_ids: newProductIds
      };
    });
  };

  _handleToggleAll = () => {
    this.setState(prevState => {
      const hasSelectedCarriers = prevState.carrier_ids.length > 0;

      if (hasSelectedCarriers) {
        // Deselect all
        return {
          carrier_ids: [],
          product_ids: []
        };
      } else {
        // Select all carriers based on activity type
        const carriersToUse =
          prevState.firelight_activity === 'simplified_eticket'
            ? prevState.vive_filtered_carriers
            : prevState.filtered_carriers;

        const allCarrierIds = carriersToUse.map(c => c.id);
        const allProductIds = carriersToUse.flatMap(c =>
          c.products ? c.products.map(p => p.id) : []
        );

        return {
          carrier_ids: allCarrierIds,
          product_ids: allProductIds
        };
      }
    });
  };
  _handleFilterCarriers = () => {
    const {
      action_type,
      carriers = [],
      agency_specific_carriers = [],
      viveCarriers = [],
      firelight_carrier_ids = [],
      firelight_loaded = false
    } = this.state;

    // For firelight, if we haven't loaded firelight products yet, don't filter
    if (action_type === 'firelight' && !firelight_loaded) {
      return;
    }

    // Define carrier filter conditions
    const filterConditions = {
      applicant: c => c.applicint_id,
      ethos: c => c.ethos_id,
      psg_eapp: c => c.products && c.products.some(p => p.psg_id),
      firelight: c => firelight_carrier_ids.includes(c.id),
      protective: c => c.id === protective_carrier_id || c.id === liberty_mutual_carrier_id,
      sbli: c => c.id === sbli_carrier_id || c.id === centrian_carrier_id,
      nmb_term_eticket: c => c.id === nmb_carrier_id,
      nmb_whole_life_eticket: c => c.id === nmb_carrier_id,
      anico: c => c.id === anico_carrier_id,
      paclife: c => c.id === paclife_carrier_id
    };

    const actionTypes = Object.keys(filterConditions);
    let filtered_carriers = [];
    let vive_filtered_carriers = [];

    if (actionTypes.includes(action_type)) {
      // Get all available carriers (agency specific + general)
      const allCarriers = [...agency_specific_carriers, ...carriers];

      // Filter based on action type
      const filterFn = filterConditions[action_type];

      if (
        action_type === 'firelight' &&
        (!firelight_loaded || firelight_carrier_ids.length === 0)
      ) {
        filtered_carriers = allCarriers;
        vive_filtered_carriers = viveCarriers;
      } else {
        filtered_carriers = allCarriers.filter(filterFn);
        if (this.state.firelight_activity === 'simplified_eticket') {
          vive_filtered_carriers = viveCarriers;
        } else {
          vive_filtered_carriers = viveCarriers.filter(filterFn);
        }
      }

      // Remove duplicates based on carrier ID for regular carriers
      const uniqueCarriers = [];
      const seenIds = new Set();

      filtered_carriers.forEach(carrier => {
        if (!seenIds.has(carrier.id)) {
          seenIds.add(carrier.id);
          uniqueCarriers.push(carrier);
        }
      });
      filtered_carriers = uniqueCarriers;

      // Remove duplicates based on carrier ID for Vive carriers
      const uniqueViveCarriers = [];
      const seenViveIds = new Set();

      vive_filtered_carriers.forEach(carrier => {
        if (!seenViveIds.has(carrier.id)) {
          seenViveIds.add(carrier.id);
          uniqueViveCarriers.push(carrier);
        }
      });
      vive_filtered_carriers = uniqueViveCarriers;
    } else {
      filtered_carriers = carriers;
      vive_filtered_carriers = viveCarriers; // Use all Vive carriers for non-firelight actions
    }

    this.setState(prevState => {
      let newCarrierIds = prevState.carrier_ids;

      if (action_type === 'firelight') {
        const carriersToCheck =
          this.state.firelight_activity === 'simplified_eticket'
            ? vive_filtered_carriers
            : filtered_carriers;

        //existing selections that are still in filtered carriers
        const validExistingIds = prevState.carrier_ids.filter(id =>
          carriersToCheck.some(fc => fc.id === id)
        );

        // For simplified_eticket, preserve user selections; for others, auto-select if none exist
        if (this.state.firelight_activity === 'simplified_eticket') {
          newCarrierIds = validExistingIds;
        } else {
          newCarrierIds =
            validExistingIds.length > 0 ? validExistingIds : carriersToCheck.map(fc => fc.id);
        }
      } else {
        // For non-firelight actions, select all filtered carriers
        newCarrierIds = filtered_carriers.map(fc => fc.id);
      }

      return {
        filtered_carriers,
        vive_filtered_carriers,
        carrier_ids: newCarrierIds
      };
    });
  };

  render() {
    const {
      action_type,
      carrier_ids,
      error_msg,
      label,
      max_face_amount,
      min_face_amount,
      open_in_modal,
      order,
      product_ids,
      success_msg,
      url,

      agency_specific_carriers,
      firelight_activity,
      firelight_product_ids,

      errors,
      firelight_error,
      firelight_loaded,
      loading_carriers
    } = this.state;
    const {
      actionTypes,
      agency,
      hideIcon,
      onClose,
      quoteAction,
      showFaceAmounts,
      showOpenInModal
    } = this.props;
    const styles = this.styles();
    const is_applicint = action_type === 'applicant';
    const is_ethos = action_type === 'ethos';
    const is_psg_eapp = action_type === 'psg_eapp';
    const is_ixn_lead_form = action_type === 'ixn_lead_form';
    const is_firelight = action_type === 'firelight';
    const is_webhook_post = action_type === 'webhook_post';
    const action = QUOTE_ACTIONS.find(a => a.value === action_type);
    const is_default_alq_action = action && action.group ? action.group === 'Default' : '';
    const firelight_enabled = agency.fl_environment;

    const buttons =
      is_firelight && !firelight_enabled
        ? []
        : [
            {
              disabled: !carrier_ids.length,
              children: 'Save',
              onClick: this._handleSaveClick.bind(null, this.state.filtered_carriers),
              style: { marginLeft: 'auto' }
            }
          ];

    return (
      <Modal
        buttons={buttons}
        maxWidth={1400}
        onClose={onClose}
        title={quoteAction ? 'Editing Quote Action' : 'New Quote Action'}
      >
        <div style={styles.modal_content}>
          <div style={styles.fields}>
            {hideIcon ? null : <Field label='Icon' />}

            <Field
              helpLabel='Integration Guides'
              helpLink='http://support.ixntech.com/how-to-set-up-quote-actions-in-the-agency-life-quoter'
              label='Type'
            >
              <Select
                onChange={this._handleActionTypeChange}
                optionGroups={actionTypes}
                value={action_type}
              />
            </Field>

            <div style={{ display: 'flex' }}>
              <div style={{ marginRight: 10, flex: 1 }}>
                <Field label='Label'>
                  <Input
                    invalid={errors.includes('label')}
                    onChange={this._handleFieldChange.bind(null, 'label')}
                    placeholder='eg: Visit IXN'
                    required={true}
                    value={label}
                  />
                </Field>
              </div>

              <div style={{ width: 100 }}>
                <Field label='Display Order'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'order')}
                    placeholder='eg: 1'
                    type='number'
                    value={order}
                  />
                </Field>
              </div>
            </div>

            {is_applicint ||
            is_psg_eapp ||
            is_ixn_lead_form ||
            is_default_alq_action ||
            is_firelight ||
            is_ethos ? null : (
              <>
                <Field label='URL'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'url')}
                    placeholder='eg: https://ixn.tech'
                    value={url}
                  />
                </Field>
              </>
            )}

            {showOpenInModal &&
            !is_default_alq_action &&
            !is_psg_eapp &&
            !is_firelight &&
            !is_ethos ? (
              <div style={{ marginBottom: 20 }}>
                <Checkbox
                  checked={open_in_modal}
                  label='Open inside quoter popup modal'
                  name='open_in_modal'
                  onChange={this._handleCheckboxChange.bind(null, 'open_in_modal')}
                  type='checkbox'
                />{' '}
              </div>
            ) : null}

            {showFaceAmounts ? (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ marginRight: 10 }}>
                  <Field label='Min Face Amount'>
                    <Input
                      onChange={this._handleFieldChange.bind(null, 'min_face_amount')}
                      placeholder='eg: 50000'
                      type='number'
                      value={min_face_amount}
                    />
                  </Field>
                </div>

                <div>
                  <Field label='Max Face Amount'>
                    <Input
                      onChange={this._handleFieldChange.bind(null, 'max_face_amount')}
                      placeholder='eg: 1000000'
                      type='number'
                      value={max_face_amount}
                    />
                  </Field>
                </div>
              </div>
            ) : null}

            {is_webhook_post ? (
              <>
                <Field label='Success Message'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'success_msg')}
                    type='textarea'
                    value={success_msg}
                  />
                </Field>
                <Field label='Error Message'>
                  <Input
                    onChange={this._handleFieldChange.bind(null, 'error_msg')}
                    type='textarea'
                    value={error_msg}
                  />
                </Field>
              </>
            ) : null}

            {is_firelight && firelight_enabled ? (
              <FieldGroup contained={true} label='FireLight Configuration'>
                <Field label='Activity'>
                  <Select
                    onChange={this._handleFieldChange.bind(null, 'firelight_activity')}
                    options={[{ value: '', label: '-- Select One --' }].concat(
                      FIRELIGHT_ACTIVITIES
                    )}
                    value={firelight_activity || 'e_app'}
                  />
                </Field>
                <Button onClick={this._getFirelightProducts} style={{ width: '100%' }}>
                  Load Available Carriers
                </Button>
              </FieldGroup>
            ) : null}
          </div>

          <div style={styles.content}>
            {loading_carriers ? (
              <Loader />
            ) : (
              <>
                {is_firelight ? (
                  <>
                    {firelight_enabled ? (
                      <>
                        {firelight_loaded ? (
                          <>
                            {firelight_error ? (
                              <ZeroState icon='alert' title='Connection Error' type='error'>
                                We weren’t able to connect to FireLight with the information you
                                provided. Please double check your FireLight integration
                                configuration details located here and try again. If you continue to
                                have problems, please contact us at support@ixn.tech.
                              </ZeroState>
                            ) : (
                              <>
                                {this.state.firelight_products.length ? (
                                  <div style={styles.carriers_panel}>
                                    <Button
                                      inline={true}
                                      onClick={this._handleToggleAll}
                                      style={styles.button}
                                    >
                                      {carrier_ids.length ? 'Select None' : 'Select All'}
                                    </Button>
                                    <div style={styles.carriers}>
                                      {(this.state.firelight_activity === 'simplified_eticket'
                                        ? this.state.vive_filtered_carriers
                                        : this.state.filtered_carriers
                                      ).map(carrier => {
                                        const is_selected = carrier_ids.includes(carrier.id);
                                        let products = carrier.products.filter(p =>
                                          this.props.availableProductIds?.includes(p.id)
                                        );

                                        if (
                                          this.state.firelight_activity !== 'simplified_eticket'
                                        ) {
                                          products = carrier.products.filter(
                                            p =>
                                              p.cusip &&
                                              this.props.availableProductIds?.includes(p.id)
                                          );
                                        }

                                        return (
                                          <CarrierCard
                                            carrier={carrier}
                                            isSelected={is_selected}
                                            key={carrier.id}
                                            onClick={this._handleCarrierToggle.bind(
                                              null,
                                              carrier,
                                              is_selected
                                            )}
                                            onProductsSave={this._handleSaveProducts}
                                            products={products}
                                            selectedProductIds={product_ids}
                                          />
                                        );
                                      })}
                                    </div>
                                  </div>
                                ) : (
                                  <ZeroState
                                    icon='folder-open-outline'
                                    title='No Available Carriers'
                                  >
                                    There are no carriers that support this quote action
                                    configuration.
                                  </ZeroState>
                                )}
                              </>
                            )}
                          </>
                        ) : (
                          <ZeroState icon='folder-open-outline' title='No Carriers Loaded'>
                            Select an Activity to the left and click “Load Available Carriers”
                          </ZeroState>
                        )}
                      </>
                    ) : (
                      <div
                        style={{
                          fontSize: FontSizes.REGULAR,
                          lineHeight: '1.6em',
                          textAlign: 'center',
                          padding: '40px 60px 0'
                        }}
                      >
                        <img
                          alt='Firelight Logo'
                          src='/img/logos/firelight.png'
                          style={{ width: 200, marginBottom: 20 }}
                        />
                        <div style={{ fontStyle: 'italic', marginBottom: 40 }}>
                          FireLight® is a multi-carrier, uniquely flexible electronic application,
                          order entry sales solution that manages the application data collection
                          and submission process, generating in-good-order business processing.
                          Interested in learning more? Contact us at sales@ixn.tech.
                        </div>
                        <div style={{ fontWeight: 600 }}>
                          To enable this quote action, you must first configurate Firelight under
                          the Agency Integrations page. After it’s been configured you can return to
                          this screen to set up the quote action.
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div style={styles.carriers_panel}>
                    <Button inline={true} onClick={this._handleToggleAll} style={styles.button}>
                      {carrier_ids.length ? 'Select None' : 'Select All'}
                    </Button>

                    <div style={styles.carriers}>
                      {this.state.filtered_carriers.map(carrier => {
                        const is_selected = carrier_ids.includes(carrier.id);

                        let products = carrier.products.filter(p =>
                          this.props.availableProductIds.includes(p.id)
                        );

                        if (is_applicint) {
                          products = products.filter(p => p.applicint_id && p.applicint_name);
                        } else if (is_psg_eapp) {
                          products = products.filter(p => p.psg_id);
                        } else if (is_firelight) {
                          products = products.filter(p => firelight_product_ids.includes(p.id));
                        }

                        return (
                          <CarrierCard
                            carrier={carrier}
                            isSelected={is_selected}
                            key={carrier.id}
                            onClick={this._handleCarrierToggle.bind(null, carrier, is_selected)}
                            onProductsSave={this._handleSaveProducts}
                            products={products}
                            selectedProductIds={product_ids}
                          />
                        );
                      })}
                    </div>

                    {agency_specific_carriers?.length ? (
                      <Field label='Agency Specific Carriers'>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                          {agency_specific_carriers.map(carrier => {
                            const is_selected = carrier_ids.includes(carrier.id);

                            return (
                              <CarrierCard
                                carrier={carrier}
                                isSelected={is_selected}
                                key={carrier.id}
                                onClick={this._handleCarrierToggle.bind(null, carrier, is_selected)}
                                onProductsSave={this._handleSaveProducts}
                                products={carrier.products}
                                selectedProductIds={product_ids}
                              />
                            );
                          })}
                        </div>
                      </Field>
                    ) : null}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>
    );
  }

  styles = () => {
    return {
      modal_content: {
        display: 'flex',
        justifyContent: 'space-between',
        overflow: 'hidden'
      },

      fields: {
        width: 400,
        flexShrink: 0,
        padding: 40,
        height: 'calc(100vh - 260px)',
        overflow: 'auto'
      },
      icon: {
        marginBottom: 10,
        cursor: 'copy',
        maxWidth: 150
      },

      content: {
        overflow: 'auto',
        width: 'calc(100vw - 400px)',
        height: 'calc(100vh - 260px)'
      },
      carriers_panel: {
        padding: '40px 30px'
      },
      carriers: {
        display: 'flex',
        flexWrap: 'wrap'
      },

      button: {
        marginBottom: 20,
        marginLeft: 10
      }
    };
  };
}

export default EditQuoteActionModal;
